import React, { useState, useEffect, useMemo, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import AirportInput from "@/components/AirportInput";
import { Plane, Loader2, ArrowRight, Users, ArrowLeftRight, Calendar, Clock, Plus, X as XIcon, Route, ShieldAlert, Gauge } from "lucide-react";
import QuoteGlobe from "@/components/QuoteGlobe";
import { computeDutyPlan, formatWallTime, formatHM, DUTY_RULES } from "@/lib/dutyTimeline";
import { useAuth } from "@/context/AuthContext";
import { stashPendingRfq } from "@/lib/pendingRfq";

/**
 * InstantQuoteWidget
 * Fetches the full aircraft-type catalog from /api/aircraft-types/catalog
 * on mount so shoppers can price ANY type — even ones we don't fly yet.
 * That "unavailable-but-still-priced" signal is gold for BD.
 */
export default function InstantQuoteWidget({ compact = false, defaults = {} }) {
  const _today = new Date();
  const _nextHour = String(Math.min(23, _today.getHours() + 1)).padStart(2, "0");
  const _todayIso = _today.toISOString().slice(0, 10);

  const [origin, setOrigin] = useState(defaults.origin || "");
  const [destination, setDestination] = useState(defaults.destination || "");
  const [typeCode, setTypeCode] = useState(defaults.typeCode || "EMB-505");
  const [pax, setPax] = useState(defaults.pax || 4);
  const [returnTrip, setReturnTrip] = useState(!!defaults.returnTrip);
  const [depDate, setDepDate] = useState(defaults.depDate || _todayIso);
  const [depHour, setDepHour] = useState(defaults.depHour || _nextHour);
  const [busy, setBusy] = useState(false);
  const [quote, setQuote] = useState(null);
  const [catalog, setCatalog] = useState(null);

  // Multi-leg mode — same aircraft stays with pax across multiple legs.
  const [mode, setMode] = useState("single");  // "single" | "multi"
  const [legs, setLegs] = useState([
    { origin: "", destination: "", date: _todayIso, hour: _nextHour },
    { origin: "", destination: "", date: _todayIso, hour: _nextHour },
  ]);

  useEffect(() => {
    api.get("/aircraft-types/catalog")
      .then((r) => setCatalog(r.data.catalog))
      .catch(() => setCatalog([])); // fall back to empty; select will still render defaults
  }, []);

  /* ---------- Aircraft cruise speed for the currently selected type ---------- */
  const selectedAircraft = useMemo(() => {
    const flat = (catalog || []).flatMap((g) => g.types || []);
    return flat.find((t) => t.code === typeCode) || null;
  }, [catalog, typeCode]);
  const cruiseKtas = selectedAircraft?.cruise_ktas || 0;

  /* ---------- Resolve every ICAO the user has entered into { lat, lng } ---------- *
   * We cache in a ref so switching between single/multi mode does not re-hit the
   * network for airports we already know. The duty timeline needs these coords
   * to compute great-circle distance → flight time per leg.                       */
  const coordCacheRef = useRef({});
  const [coords, setCoords] = useState({});

  useEffect(() => {
    const wanted = mode === "multi"
      ? Array.from(new Set(legs.flatMap((l) => [l.origin, l.destination]).filter(Boolean)))
      : [origin, destination].filter(Boolean);
    const missing = wanted.filter((c) => c && !coordCacheRef.current[c.toUpperCase()]);
    if (missing.length === 0) {
      // Still expose current cache so consumers can re-render if legs change.
      setCoords({ ...coordCacheRef.current });
      return;
    }
    let cancelled = false;
    (async () => {
      for (const icao of missing) {
        const up = icao.toUpperCase();
        try {
          const r = await api.get("/airports/search", { params: { q: up, limit: 1 } });
          const hit = (r.data?.results || [])[0];
          if (hit && hit.lat != null && hit.lng != null) {
            coordCacheRef.current[up] = {
              lat: Number(hit.lat),
              lng: Number(hit.lng),
              city: hit.city || hit.name || up,
            };
          }
        } catch { /* leave missing → plan marks that leg pending */ }
      }
      if (!cancelled) setCoords({ ...coordCacheRef.current });
    })();
    return () => { cancelled = true; };
  }, [mode, JSON.stringify(legs.map((l) => (l.origin || "") + "|" + (l.destination || ""))), origin, destination]);

  /* ---------- Live duty timeline (multi-leg only) ---------- */
  const dutyPlan = useMemo(() => {
    if (mode !== "multi" || !cruiseKtas) return null;
    return computeDutyPlan(legs, cruiseKtas, coords);
  }, [mode, legs, cruiseKtas, coords]);

  const submit = async (e) => {
    e?.preventDefault?.();
    setBusy(true);
    setQuote(null);
    try {
      let r;
      if (mode === "multi") {
        const validLegs = legs.filter((l) => l.origin && l.destination);
        if (validLegs.length < 2) {
          toast.error("Add at least 2 legs with origin + destination");
          setBusy(false);
          return;
        }
        r = await api.post("/quotes/compute-multileg", {
          legs: validLegs.map((l) => ({
            origin_icao: l.origin,
            destination_icao: l.destination,
            departure_date: l.date,
            departure_hour: l.hour,
            passengers: l.passengers ? Number(l.passengers) : null,
          })),
          type_code: typeCode,
          passengers: Number(pax) || 1,
        });
      } else {
        if (!origin || !destination) {
          toast.error("Pick both an origin and destination airport");
          setBusy(false);
          return;
        }
        if (origin === destination) {
          toast.error("Origin and destination must differ");
          setBusy(false);
          return;
        }
        const departureIso = depDate ? `${depDate}T${depHour}:00:00` : undefined;
        r = await api.post("/quotes/compute", {
          origin_icao: origin,
          destination_icao: destination,
          type_code: typeCode,
          passengers: Number(pax) || 1,
          return_trip: returnTrip,
          departure_date: departureIso,
        });
      }
      setQuote({ mode, data: r.data });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Quote failed. Try different airports.");
    } finally {
      setBusy(false);
    }
  };

  const swap = () => { const o = origin; setOrigin(destination); setDestination(o); };

  return (
    <div className="w-full" data-testid="instant-quote-widget">
      <form
        onSubmit={submit}
        className={`rounded-xl border border-cyan/30 p-4 md:p-5 relative overflow-hidden ${compact ? "" : "shadow-[0_0_40px_rgba(75,156,255,0.10)]"}`}
        style={{ background: "linear-gradient(155deg, rgba(75,156,255,0.06) 0%, rgba(5,8,16,0.85) 60%)" }}
      >
        <div className="flex items-center gap-2 mb-3 flex-wrap">
          <Plane className="h-4 w-4 text-cyan" />
          <div className="mono text-[10px] tracking-[0.24em] uppercase text-cyan/80">// Instant PJ Quote</div>
          <div className="ml-auto inline-flex rounded-full border border-white/15 p-0.5 bg-white/[0.02]" data-testid="quote-mode-toggle">
            <button
              type="button"
              onClick={() => setMode("single")}
              data-testid="quote-mode-single"
              className={`mono text-[10px] tracking-widest uppercase px-3 py-1 rounded-full ${mode === "single" ? "bg-cyan text-obsidian" : "text-white/60 hover:text-white"}`}
            >Single trip</button>
            <button
              type="button"
              onClick={() => setMode("multi")}
              data-testid="quote-mode-multi"
              className={`mono text-[10px] tracking-widest uppercase px-3 py-1 rounded-full flex items-center gap-1 ${mode === "multi" ? "bg-cyan text-obsidian" : "text-white/60 hover:text-white"}`}
            ><Route className="h-3 w-3" /> Multi-leg</button>
          </div>
        </div>

        {/* ------------ SINGLE mode ------------ */}
        {mode === "single" && (
          <SingleTripInputs
            origin={origin} setOrigin={setOrigin}
            destination={destination} setDestination={setDestination}
            depDate={depDate} setDepDate={setDepDate}
            depHour={depHour} setDepHour={setDepHour}
            todayIso={_todayIso}
            onSwap={() => { const o = origin; setOrigin(destination); setDestination(o); }}
          />
        )}

        {/* ------------ MULTI mode ------------ */}
        {mode === "multi" && (
          <MultiLegInputs legs={legs} setLegs={setLegs} todayIso={_todayIso} dutyPlan={dutyPlan} aircraftLabel={selectedAircraft?.label || typeCode} cruiseKtas={cruiseKtas} />
        )}

        {/* Aircraft + pax + return */}
        <div className="grid md:grid-cols-[1fr_140px_auto] gap-2 mt-3 items-end">
          <div>
            <div className="mono text-[10px] tracking-widest uppercase text-white/50 mb-1">Aircraft type</div>
            <select
              data-testid="quote-type"
              value={typeCode}
              onChange={(e) => setTypeCode(e.target.value)}
              className="input w-full"
            >
              {(catalog && catalog.length > 0) ? (
                catalog.map((group) => (
                  <optgroup key={group.oem} label={group.oem}>
                    {group.types.map((t) => (
                      <option key={t.code} value={t.code}>
                        {t.label}{t.cruise_ktas ? ` · ${t.cruise_ktas} kt` : ""}
                      </option>
                    ))}
                  </optgroup>
                ))
              ) : (
                <option value="EMB-505">Phenom 300 · Light</option>
              )}
            </select>
          </div>
          <div>
            <div className="mono text-[10px] tracking-widest uppercase text-white/50 mb-1 flex items-center gap-1">
              <Users className="h-3 w-3" /> Passengers
            </div>
            <input
              data-testid="quote-pax"
              type="number"
              min="1"
              max="20"
              value={pax}
              onChange={(e) => setPax(e.target.value)}
              className="input w-full"
            />
          </div>
          <label className={`mono text-[10px] tracking-widest uppercase text-white/70 flex items-center gap-2 pb-2 md:pb-3 cursor-pointer select-none ${mode === "multi" ? "opacity-40 pointer-events-none" : ""}`}>
            <input
              data-testid="quote-return"
              type="checkbox"
              checked={returnTrip}
              onChange={(e) => setReturnTrip(e.target.checked)}
              className="accent-cyan"
              disabled={mode === "multi"}
            /> Round-trip
          </label>
        </div>

        <div className="mt-4 flex items-center justify-between gap-3 flex-wrap">
          <div className="text-[11px] text-white/50 max-w-md">
            Instant estimate powered by cruise-speed × hourly-rate math. Final quote may vary with positioning, crew day, and ramp fees.
          </div>
          <button
            type="submit"
            disabled={busy || (mode === "single" && (!origin || !destination))}
            data-testid="quote-submit"
            className="mono text-[10px] tracking-widest uppercase px-5 py-2.5 rounded bg-cyan text-obsidian font-bold hover:brightness-110 disabled:opacity-40 flex items-center gap-1.5"
          >
            {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <ArrowRight className="h-3.5 w-3.5" />}
            {busy ? "COMPUTING…" : "GET QUOTE"}
          </button>
        </div>
      </form>

      {quote && (quote.mode === "multi"
        ? <MultiLegResult data={quote.data} />
        : <QuoteResult quote={quote.data} />
      )}

      {/* Live 3D route preview — pins update as ICAOs resolve; jet flies the newest arc on aircraft select. */}
      <div className="mt-4">
        <QuoteGlobe
          airports={
            mode === "multi"
              ? legs.filter((l) => l.origin || l.destination).flatMap((l, i) => {
                  // Preserve continuity: only add the origin of the FIRST leg,
                  // then just the destinations of every leg after (a→b→c→d).
                  const out = [];
                  if (i === 0 && l.origin) out.push({ icao: l.origin });
                  if (l.destination) out.push({ icao: l.destination });
                  return out;
                })
              : [origin, destination].filter(Boolean).map((icao) => ({ icao }))
          }
          aircraftLabel={
            catalog?.flatMap((g) => g.types).find((t) => t.code === typeCode)?.label || typeCode
          }
        />
      </div>
    </div>
  );
}

/* ============================ SINGLE mode ============================ */

function SingleTripInputs({ origin, setOrigin, destination, setDestination, depDate, setDepDate, depHour, setDepHour, todayIso, onSwap }) {
  return (
    <>
      <div className="grid md:grid-cols-[1fr_auto_1fr] gap-2 items-end">
        <div>
          <div className="mono text-[10px] tracking-widest uppercase text-white/50 mb-1">From</div>
          <AirportInput testId="quote-origin" value={origin} onChange={setOrigin} placeholder="ICAO / city (e.g. KMIA, Miami)" />
        </div>
        <button type="button" onClick={onSwap} data-testid="quote-swap"
          className="mono text-[10px] tracking-widest uppercase px-2 py-1.5 rounded border border-white/20 text-white/70 hover:bg-white/5 md:mb-1 flex items-center gap-1 h-fit self-end"
          title="Swap origin and destination">
          <ArrowLeftRight className="h-3 w-3" />
        </button>
        <div>
          <div className="mono text-[10px] tracking-widest uppercase text-white/50 mb-1">To</div>
          <AirportInput testId="quote-destination" value={destination} onChange={setDestination} placeholder="ICAO / city (e.g. KTEB, Teterboro)" />
        </div>
      </div>
      <div className="flex flex-wrap gap-2 mt-3">
        <div className="flex-shrink-0" style={{ maxWidth: 200 }}>
          <div className="mono text-[10px] tracking-widest uppercase text-white/50 mb-1 flex items-center gap-1">
            <Calendar className="h-3 w-3 text-cyan" /> Departure date
          </div>
          <input data-testid="quote-date" type="date" value={depDate} onChange={(e) => setDepDate(e.target.value)}
            min={todayIso} className="input quote-date-input" style={{ width: 190 }} />
        </div>
        <div className="flex-shrink-0" style={{ maxWidth: 160 }}>
          <div className="mono text-[10px] tracking-widest uppercase text-white/50 mb-1 flex items-center gap-1">
            <Clock className="h-3 w-3 text-cyan" /> Departure hour
          </div>
          <select data-testid="quote-hour" value={depHour} onChange={(e) => setDepHour(e.target.value)} className="input" style={{ width: 150 }}>
            {Array.from({ length: 24 }, (_, i) => {
              const h24 = String(i).padStart(2, "0");
              const h12 = ((i + 11) % 12) + 1;
              const ampm = i < 12 ? "AM" : "PM";
              return <option key={h24} value={h24}>{h24}:00  ·  {h12}:00 {ampm}</option>;
            })}
          </select>
        </div>
      </div>
    </>
  );
}

/* ============================ MULTI mode ============================ */

function MultiLegInputs({ legs, setLegs, todayIso, dutyPlan, aircraftLabel, cruiseKtas }) {
  /**
   * When the user sets a leg's destination → auto-fill next leg's origin.
   * When the user sets a leg's date → cascade the same date forward to any
   * subsequent leg whose date is still empty OR earlier than this leg's date
   * (protects the customer from an illegal backwards date if they miss it).
   */
  const updateLeg = (idx, patch) =>
    setLegs((ls) =>
      ls.map((l, i) => {
        if (i === idx) return { ...l, ...patch };
        if (i > idx) {
          const next = { ...l };
          // Continuity: origin follows previous destination when empty.
          if (i === idx + 1 && patch.destination && !next.origin) {
            next.origin = patch.destination;
          }
          // Date cascade: newer legs cannot precede this leg's date.
          if (patch.date) {
            if (!next.date || next.date < patch.date) {
              next.date = patch.date;
            }
          }
          return next;
        }
        return l;
      }),
    );
  const addLeg = () => setLegs((ls) => {
    const last = ls[ls.length - 1] || {};
    return [...ls, { origin: last.destination || "", destination: "", date: last.date || todayIso, hour: last.hour || "10" }];
  });
  const removeLeg = (idx) => setLegs((ls) => ls.length > 2 ? ls.filter((_, i) => i !== idx) : ls);

  return (
    <div className="space-y-2" data-testid="quote-multi-legs">
      {legs.map((leg, idx) => {
        const plan = dutyPlan?.plans?.[idx];
        return (
          <div
            key={idx}
            data-testid={`quote-leg-${idx}`}
            className="rounded-lg border border-white/10 bg-white/[0.02] p-2.5"
          >
            <div className="grid md:grid-cols-[24px_1fr_1fr_180px_160px_24px] gap-2 items-end">
              <div className="mono text-[10px] tracking-widest uppercase text-cyan/80 md:pt-6">L{idx + 1}</div>
              <div>
                <div className="mono text-[10px] tracking-widest uppercase text-white/50 mb-1">From</div>
                <AirportInput testId={`quote-leg-${idx}-from`} value={leg.origin} onChange={(v) => updateLeg(idx, { origin: v })} placeholder="ICAO" />
              </div>
              <div>
                <div className="mono text-[10px] tracking-widest uppercase text-white/50 mb-1">To</div>
                <AirportInput testId={`quote-leg-${idx}-to`} value={leg.destination} onChange={(v) => updateLeg(idx, { destination: v })} placeholder="ICAO" />
              </div>
              <div>
                <div className="mono text-[10px] tracking-widest uppercase text-white/50 mb-1 flex items-center gap-1"><Calendar className="h-3 w-3 text-cyan" /> Date</div>
                <input
                  data-testid={`quote-leg-${idx}-date`}
                  type="date"
                  value={leg.date}
                  min={idx === 0 ? todayIso : (legs[idx - 1]?.date || todayIso)}
                  onChange={(e) => updateLeg(idx, { date: e.target.value })}
                  className="input quote-date-input"
                  style={{ width: 170 }}
                />
              </div>
              <div>
                <div className="mono text-[10px] tracking-widest uppercase text-white/50 mb-1 flex items-center gap-1"><Clock className="h-3 w-3 text-cyan" /> Hour</div>
                <select
                  data-testid={`quote-leg-${idx}-hour`}
                  value={leg.hour}
                  onChange={(e) => updateLeg(idx, { hour: e.target.value })}
                  className="input"
                  style={{ width: 150 }}
                >
                  {Array.from({ length: 24 }, (_, i) => {
                    const h24 = String(i).padStart(2, "0");
                    const h12 = ((i + 11) % 12) + 1;
                    const ampm = i < 12 ? "AM" : "PM";
                    return <option key={h24} value={h24}>{h24}:00  ·  {h12}:00 {ampm}</option>;
                  })}
                </select>
              </div>
              <button
                type="button"
                onClick={() => removeLeg(idx)}
                disabled={legs.length <= 2}
                data-testid={`quote-leg-${idx}-remove`}
                className="mono text-[10px] tracking-widest uppercase p-1.5 rounded border border-white/15 text-white/50 hover:bg-red-500/10 hover:text-red-300 hover:border-red-400/40 disabled:opacity-30 disabled:pointer-events-none md:mb-1 self-end"
                title="Remove leg"
              >
                <XIcon className="h-3 w-3" />
              </button>
            </div>

            {/* Per-leg duty timeline (live) */}
            <DutyLegPanel
              leg={leg}
              plan={plan}
              aircraftLabel={aircraftLabel}
              cruiseKtas={cruiseKtas}
              legIndex={idx}
            />
          </div>
        );
      })}
      <button
        type="button"
        onClick={addLeg}
        disabled={legs.length >= 12}
        data-testid="quote-add-leg"
        className="mono text-[10px] tracking-widest uppercase px-3 py-2 rounded border border-dashed border-cyan/40 text-cyan hover:bg-cyan/5 disabled:opacity-40 flex items-center gap-1.5"
      >
        <Plus className="h-3 w-3" /> ADD ANOTHER LEG
      </button>

      {/* Trip-wide duty summary */}
      <DutyTripSummary dutyPlan={dutyPlan} aircraftLabel={aircraftLabel} cruiseKtas={cruiseKtas} />

      <div className="text-[11px] text-white/45 mt-1">
        Aircraft + crew stay with you across all legs. Daily 2h minimums apply unless the trip totals ≥10h · $800 crew overnight per night away from base.
      </div>
    </div>
  );
}

/* ---------- Per-leg live duty timeline ---------- */
function DutyLegPanel({ leg, plan, aircraftLabel, cruiseKtas, legIndex }) {
  // Nothing to show until the leg has origin, destination, and cruise speed.
  if (!plan) {
    return null;
  }
  if (!plan.ready) {
    if (plan.pending) {
      return (
        <div className="mt-2 mono text-[10px] tracking-widest uppercase text-white/35 flex items-center gap-1.5" data-testid={`quote-leg-${legIndex}-duty-pending`}>
          <Loader2 className="h-3 w-3 animate-spin" /> Resolving airports…
        </div>
      );
    }
    return (
      <div className="mt-2 mono text-[10px] tracking-widest uppercase text-white/35" data-testid={`quote-leg-${legIndex}-duty-empty`}>
        // Fill origin + destination to compute duty
      </div>
    );
  }

  const {
    dutyStartMs, blockOffMs, blockOnMs, dutyEndMs,
    flightHrs, distNm,
    cumFlightHrs, cumDutyHrs,
    remainingFlight, remainingDuty,
    flightBreach, dutyBreach, flightWarn, dutyWarn,
    newPeriod, restHrsSincePrev,
  } = plan;

  const flightColor = flightBreach ? "text-red-300" : flightWarn ? "text-amber-300" : "text-avail";
  const dutyColor = dutyBreach ? "text-red-300" : dutyWarn ? "text-amber-300" : "text-avail";

  return (
    <div className="mt-3 rounded border border-cyan/20 bg-cyan/[0.04] p-2.5" data-testid={`quote-leg-${legIndex}-duty`}>
      {/* Rest / new period marker */}
      {legIndex > 0 && (
        <div className={`mono text-[9px] tracking-[0.24em] uppercase mb-2 flex items-center gap-1.5 ${newPeriod ? "text-avail" : "text-cyan/70"}`}>
          {newPeriod ? (
            <>
              <ShieldAlert className="h-3 w-3" />
              // NEW DUTY PERIOD · {restHrsSincePrev != null ? `${formatHM(restHrsSincePrev)} rest` : "rest ≥ 10h"}
            </>
          ) : (
            <>
              <Gauge className="h-3 w-3" />
              // Continues previous duty period
            </>
          )}
        </div>
      )}

      {/* Timeline row */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
        <TimelinePin label="Duty in" sublabel="–1:00" value={formatWallTime(dutyStartMs)} tone="cyan" testId={`quote-leg-${legIndex}-duty-start`} />
        <TimelinePin label="Block off" sublabel="wheels up" value={formatWallTime(blockOffMs)} tone="white" testId={`quote-leg-${legIndex}-block-off`} />
        <TimelinePin label="Block on" sublabel="wheels down" value={formatWallTime(blockOnMs)} tone="white" testId={`quote-leg-${legIndex}-block-on`} />
        <TimelinePin label="Duty off" sublabel="+0:30" value={formatWallTime(dutyEndMs)} tone="cyan" testId={`quote-leg-${legIndex}-duty-end`} />
      </div>

      {/* Numeric readouts */}
      <div className="mt-2.5 grid grid-cols-2 md:grid-cols-4 gap-2 text-[11px]">
        <Readout label="Leg flight" value={formatHM(flightHrs)} sub={`${Math.round(distNm)} nm @ ${cruiseKtas} kt`} tone="white" testId={`quote-leg-${legIndex}-flight-time`} />
        <Readout label="Cum flight" value={formatHM(cumFlightHrs)} sub={`of ${DUTY_RULES.maxFlight24h}h max`} tone={flightColor} testId={`quote-leg-${legIndex}-cum-flight`} />
        <Readout label="Remaining flight" value={formatHM(Math.max(0, remainingFlight))} sub={remainingFlight < 0 ? "OVER LIMIT" : "in this period"} tone={flightColor} testId={`quote-leg-${legIndex}-remain-flight`} />
        <Readout label="Remaining duty" value={formatHM(Math.max(0, remainingDuty))} sub={remainingDuty < 0 ? "OVER LIMIT" : `of ${DUTY_RULES.maxDutyPeriod}h window`} tone={dutyColor} testId={`quote-leg-${legIndex}-remain-duty`} />
      </div>

      {/* Progress bars */}
      <div className="mt-2 space-y-1.5">
        <Meter label="Flight (24h)" pct={Math.min(100, (cumFlightHrs / DUTY_RULES.maxFlight24h) * 100)} tone={flightColor} testId={`quote-leg-${legIndex}-flight-meter`} />
        <Meter label="Duty window" pct={Math.min(100, (cumDutyHrs / DUTY_RULES.maxDutyPeriod) * 100)} tone={dutyColor} testId={`quote-leg-${legIndex}-duty-meter`} />
      </div>

      {(flightBreach || dutyBreach) && (
        <div className="mt-2 rounded border border-red-500/40 bg-red-500/[0.08] p-2 mono text-[10px] tracking-widest uppercase text-red-300 flex items-start gap-1.5" data-testid={`quote-leg-${legIndex}-breach`}>
          <ShieldAlert className="h-3.5 w-3.5 mt-[1px]" />
          <span>
            FAR 135.267 breach — {flightBreach && `flight ${formatHM(cumFlightHrs)} > 10h`}
            {flightBreach && dutyBreach && " · "}
            {dutyBreach && `duty ${formatHM(cumDutyHrs)} > 14h`}
            . Add rest or trim the itinerary.
          </span>
        </div>
      )}
    </div>
  );
}

function TimelinePin({ label, sublabel, value, tone = "white", testId }) {
  const toneCls = tone === "cyan" ? "text-cyan" : "text-white";
  return (
    <div className="rounded bg-obsidian/60 border border-white/10 px-2 py-1.5" data-testid={testId}>
      <div className="flex items-center justify-between">
        <span className="mono text-[9px] tracking-[0.24em] uppercase text-white/50">{label}</span>
        <span className="mono text-[9px] tracking-widest uppercase text-white/30">{sublabel}</span>
      </div>
      <div className={`mono text-[12px] mt-0.5 ${toneCls}`}>{value}</div>
    </div>
  );
}

function Readout({ label, value, sub, tone, testId }) {
  return (
    <div className="rounded bg-obsidian/60 border border-white/10 px-2 py-1.5" data-testid={testId}>
      <div className="mono text-[9px] tracking-[0.24em] uppercase text-white/50">{label}</div>
      <div className={`mono text-[13px] mt-0.5 ${tone || "text-white"}`}>{value}</div>
      <div className="mono text-[9px] tracking-widest uppercase text-white/35 mt-0.5">{sub}</div>
    </div>
  );
}

function Meter({ label, pct, tone, testId }) {
  const bg = tone && tone.includes("red")
    ? "bg-red-400"
    : tone && tone.includes("amber")
      ? "bg-amber-300"
      : "bg-avail";
  return (
    <div data-testid={testId}>
      <div className="flex items-center justify-between mono text-[9px] tracking-[0.24em] uppercase text-white/50 mb-0.5">
        <span>{label}</span>
        <span className={tone}>{Math.round(pct)}%</span>
      </div>
      <div className="h-1.5 rounded bg-white/10 overflow-hidden">
        <div className={`h-full ${bg} transition-[width] duration-500`} style={{ width: `${Math.min(100, pct)}%` }} />
      </div>
    </div>
  );
}

function DutyTripSummary({ dutyPlan, aircraftLabel, cruiseKtas }) {
  if (!dutyPlan || !cruiseKtas) return null;
  const ready = dutyPlan.plans.filter((p) => p.ready);
  if (ready.length === 0) return null;

  // Aggregate: total flight, count distinct duty periods, peak cum flight/duty.
  const totalFlight = ready.reduce((s, p) => s + p.flightHrs, 0);
  const totalDist = ready.reduce((s, p) => s + p.distNm, 0);
  const periods = ready.filter((p) => p.newPeriod).length || 1;
  const peakFlight = Math.max(...ready.map((p) => p.cumFlightHrs));
  const peakDuty = Math.max(...ready.map((p) => p.cumDutyHrs));
  const anyBreach = ready.some((p) => p.flightBreach || p.dutyBreach);

  return (
    <div className="mt-3 rounded-lg border border-cyan/25 bg-cyan/[0.05] p-3" data-testid="quote-duty-summary">
      <div className="mono text-[10px] tracking-[0.24em] uppercase text-cyan/80 mb-2 flex items-center gap-1.5">
        <Gauge className="h-3.5 w-3.5" /> // FAR 135.267 · trip-wide tally · {aircraftLabel} @ {cruiseKtas} kt
      </div>
      <div className="grid grid-cols-2 md:grid-cols-5 gap-2 text-[11px]">
        <Readout label="Total flight" value={formatHM(totalFlight)} sub={`${Math.round(totalDist).toLocaleString()} nm`} tone="text-white" testId="quote-duty-total-flight" />
        <Readout label="Duty periods" value={String(periods)} sub={`each ≤ ${DUTY_RULES.maxDutyPeriod}h`} tone="text-white" testId="quote-duty-periods" />
        <Readout label="Peak cum flight" value={formatHM(peakFlight)} sub={`limit ${DUTY_RULES.maxFlight24h}h / 24h`} tone={peakFlight > DUTY_RULES.maxFlight24h ? "text-red-300" : peakFlight > DUTY_RULES.maxFlight24h - 1 ? "text-amber-300" : "text-avail"} testId="quote-duty-peak-flight" />
        <Readout label="Peak duty" value={formatHM(peakDuty)} sub={`window ${DUTY_RULES.maxDutyPeriod}h`} tone={peakDuty > DUTY_RULES.maxDutyPeriod ? "text-red-300" : peakDuty > DUTY_RULES.maxDutyPeriod - 1 ? "text-amber-300" : "text-avail"} testId="quote-duty-peak-duty" />
        <Readout label="Status" value={anyBreach ? "BREACH" : "LEGAL"} sub={anyBreach ? "Add rest / trim" : "As designed"} tone={anyBreach ? "text-red-300" : "text-avail"} testId="quote-duty-status" />
      </div>
    </div>
  );
}

function QuoteResult({ quote }) {
  const fmt = (n) => n.toLocaleString(undefined, { style: "currency", currency: "USD", maximumFractionDigits: 0 });
  const dep = quote.departure_local || null;
  return (
    <div
      className="mt-4 rounded-xl border border-white/10 p-5 relative overflow-hidden"
      data-testid="quote-result"
      style={{ background: "rgba(5,8,16,0.7)" }}
    >
      {/* Route header */}
      <div className="flex items-center gap-3 flex-wrap">
        <div>
          <div className="mono text-[10px] tracking-widest uppercase text-cyan/70">{quote.origin.icao}</div>
          <div className="text-sm text-white/85">{quote.origin.city}</div>
        </div>
        <Plane className="h-4 w-4 text-cyan/60 mx-1 rotate-90" />
        <div>
          <div className="mono text-[10px] tracking-widest uppercase text-cyan/70">{quote.destination.icao}</div>
          <div className="text-sm text-white/85">{quote.destination.city}</div>
        </div>
        <div className="ml-auto flex flex-wrap gap-3 text-right">
          {dep && <FactBadge k="Departure" v={dep} />}
          <FactBadge k="Distance" v={`${quote.distance_nm.toLocaleString()} nm`} />
          <FactBadge k="Flight time" v={`${quote.flight_time_hours} h`} />
          <FactBadge k="Legs" v={quote.legs} />
        </div>
      </div>

      {/* Aircraft strip */}
      <div className="mt-4 rounded-lg border border-white/[0.06] bg-white/[0.02] p-3 flex flex-wrap items-center gap-3 text-[11px]">
        <span className="mono uppercase tracking-widest text-cyan/80">{quote.aircraft.type_code}</span>
        {quote.aircraft.tail_number && (
          <span className="mono uppercase tracking-widest text-white/60">Tail {quote.aircraft.tail_number}</span>
        )}
        <span className="text-white/50">Cruise <span className="text-white">{quote.aircraft.cruise_ktas} kt</span></span>
        <span className="text-white/50">Rate <span className="text-white">${quote.aircraft.hourly_rate_usd.toLocaleString()}/hr</span></span>
        {quote.aircraft.seats && (
          <span className="text-white/50">Seats <span className="text-white">{quote.aircraft.seats}</span></span>
        )}
        <span className="ml-auto mono text-[9px] tracking-widest uppercase text-white/40">
          Rate source: {quote.aircraft.rate_source === "aircraft" ? "specific tail" : "2026 market median"}
        </span>
      </div>

      {/* Cost breakdown */}
      <div className="mt-4 grid md:grid-cols-2 gap-3">
        <div className="space-y-1.5">
          <LineItem label="Flight hours" value={fmt(quote.breakdown.flight_hours_cost_usd)} />
          <LineItem label="Federal excise tax (7.5%)" value={fmt(quote.breakdown.federal_excise_tax_usd)} muted />
          <LineItem label="Segment fee" value={fmt(quote.breakdown.segment_fee_usd)} muted />
          <LineItem label="Fuel surcharge" value={fmt(quote.breakdown.fuel_surcharge_usd)} muted />
        </div>
        <div className="rounded-lg border border-cyan/40 p-4 bg-cyan/[0.08] flex flex-col justify-center">
          <div className="mono text-[10px] tracking-widest uppercase text-cyan/80">PJP market median</div>
          <div className="font-display text-4xl font-bold text-white mt-1 leading-none" data-testid="quote-total">
            {fmt(quote.total_usd)}
          </div>
          <div className="mono text-[10px] tracking-widest uppercase text-white/50 mt-1.5">
            Subtotal {fmt(quote.subtotal_usd)}
          </div>
        </div>
      </div>

      {/* Market range band */}
      <MarketRangeBar range={quote.market_range_usd} median={quote.total_usd} />

      {/* Real-world ops factors */}
      <OpsFactorsStrip ops={quote.ops_factors} />

      {/* Operator RFQ CTA */}
      <OperatorRfqCta quote={quote} />

      {/* Notes */}
      {quote.notes?.length > 0 && (
        <ul className="mt-4 space-y-1 text-[11px] text-white/55">
          {quote.notes.map((n, i) => (
            <li key={i} className="flex gap-1.5"><span className="text-white/30">·</span>{n}</li>
          ))}
        </ul>
      )}
    </div>
  );
}

function LineItem({ label, value, muted }) {
  return (
    <div className="flex items-baseline justify-between border-b border-white/[0.04] py-1.5">
      <div className={`text-[12px] ${muted ? "text-white/50" : "text-white/85"}`}>{label}</div>
      <div className={`mono text-[13px] ${muted ? "text-white/60" : "text-white"}`}>{value}</div>
    </div>
  );
}

function FactBadge({ k, v }) {
  return (
    <div className="text-right">
      <div className="mono text-[9px] tracking-widest uppercase text-white/40">{k}</div>
      <div className="mono text-[12px] text-white">{v}</div>
    </div>
  );
}

/* ======================== MARKET RANGE BAR ======================== *
 * Displays the four operator-spread anchors (Low → Median → High → Ceiling)
 * as a horizontal band with the median highlighted. Anchored to real 2026
 * charter market data — operators quote differently based on positioning,
 * crew availability, and negotiation, so a single number lies to shoppers.
 */
function MarketRangeBar({ range, median }) {
  if (!range) return null;
  const fmt = (n) => n.toLocaleString(undefined, { style: "currency", currency: "USD", maximumFractionDigits: 0 });
  const span = range.ceiling_usd - range.low_usd;
  const pos = (val) => span > 0 ? Math.max(0, Math.min(100, ((val - range.low_usd) / span) * 100)) : 50;
  return (
    <div className="mt-4 rounded-lg border border-white/10 bg-white/[0.02] p-3.5" data-testid="quote-market-range">
      <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
        <div className="mono text-[10px] tracking-[0.24em] uppercase text-cyan/80 flex items-center gap-1.5">
          <Route className="h-3 w-3" /> // Real operator market spread
        </div>
        <div className="mono text-[9px] tracking-widest uppercase text-white/40">
          Live network anchor · 5 operator sample
        </div>
      </div>

      {/* Band */}
      <div className="relative h-8 mt-4 rounded bg-gradient-to-r from-avail/20 via-cyan/25 to-red-400/25 border border-white/10 overflow-visible">
        {/* Median marker */}
        <div
          className="absolute top-0 bottom-0 flex flex-col items-center"
          style={{ left: `${pos(range.median_usd)}%`, transform: "translateX(-50%)" }}
          data-testid="quote-market-median-marker"
        >
          <div className="w-[2px] h-full bg-cyan shadow-[0_0_8px_rgba(75,156,255,0.9)]" />
          <div className="absolute -top-4 mono text-[9px] tracking-widest uppercase text-cyan whitespace-nowrap bg-obsidian/80 px-1 rounded">Median</div>
        </div>
      </div>

      {/* Anchor readouts */}
      <div className="mt-3 grid grid-cols-2 md:grid-cols-4 gap-2 text-center">
        <RangePin label="Low" sub="Aggressive op" value={fmt(range.low_usd)} tone="text-avail" testId="quote-range-low" />
        <RangePin label="Median" sub="PJP anchor" value={fmt(range.median_usd)} tone="text-cyan" testId="quote-range-median" />
        <RangePin label="High" sub="75th pctile" value={fmt(range.high_usd)} tone="text-white" testId="quote-range-high" />
        <RangePin label="Ceiling" sub="Worst positioning" value={fmt(range.ceiling_usd)} tone="text-red-300" testId="quote-range-ceiling" />
      </div>

      <div className="mt-2.5 text-[11px] text-white/55 leading-relaxed">
        {range.band_note}
      </div>
    </div>
  );
}

function RangePin({ label, sub, value, tone, testId }) {
  return (
    <div className="rounded bg-obsidian/60 border border-white/10 px-2 py-1.5" data-testid={testId}>
      <div className="mono text-[9px] tracking-[0.24em] uppercase text-white/50">{label}</div>
      <div className={`mono text-[13px] mt-0.5 ${tone}`}>{value}</div>
      <div className="mono text-[9px] tracking-widest uppercase text-white/35 mt-0.5">{sub}</div>
    </div>
  );
}

/* ======================== OPS FACTORS STRIP ======================== *
 * Real-world signals that shape the price:
 *   • Winds aloft at cruise (Open-Meteo GFS forecast)
 *   • Fuel price at origin + destination (operator > pjp > public)
 *   • Tankering plan (if aircraft can lift extra fuel at cheap origin)
 * Everything is best-effort — if a signal is missing we just omit its cell
 * rather than break the layout. */
function OpsFactorsStrip({ ops }) {
  if (!ops) return null;
  const fmtUsd = (n) => n != null ? n.toLocaleString(undefined, { style: "currency", currency: "USD", maximumFractionDigits: 0 }) : "—";
  const wind = ops.wind;
  const fo = ops.fuel?.origin;
  const fd = ops.fuel?.destination;
  const tanker = ops.tanker;

  const windTone = !wind || !wind.forecast_valid
    ? "text-white/40"
    : wind.headwind_kt > 20 ? "text-red-300"
    : wind.headwind_kt > 5  ? "text-amber-300"
    : wind.headwind_kt < -5 ? "text-avail"
    : "text-white";
  const windValue = !wind || !wind.forecast_valid
    ? "—"
    : wind.headwind_kt > 3  ? `${Math.round(wind.headwind_kt)}kt head`
    : wind.headwind_kt < -3 ? `${Math.round(-wind.headwind_kt)}kt tail`
    : "Calm";
  const windSub = !wind || !wind.forecast_valid
    ? "forecast unavailable"
    : `Winds aloft @ FL${Math.round((wind.pressure_level_hpa ? 45000 : 41000) / 1000)}0 · ${wind.wind_speed_kt}kt from ${wind.wind_dir_deg}°`;

  const sourceLabel = (src) => src === "operator" ? "operator contract"
    : src === "pjp" ? "PJP network"
    : src === "public" ? "market baseline"
    : "—";

  return (
    <div className="mt-4 rounded-lg border border-white/10 bg-white/[0.02] p-3.5" data-testid="quote-ops-factors">
      <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
        <div className="mono text-[10px] tracking-[0.24em] uppercase text-cyan/80 flex items-center gap-1.5">
          <Gauge className="h-3 w-3" /> // Real-world ops factors
        </div>
        <div className="mono text-[9px] tracking-widest uppercase text-white/40">
          Live weather · operator fuel contracts · tanker math
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
        <OpsCell
          label="Winds aloft"
          value={windValue}
          sub={windSub}
          tone={windTone}
          testId="ops-wind"
        />
        <OpsCell
          label={`Fuel · ${fo?.airport_icao || "origin"}`}
          value={fo?.price_per_gal_usd != null ? `$${fo.price_per_gal_usd.toFixed(2)}/gal` : "—"}
          sub={fo ? sourceLabel(fo.source) : "no data"}
          tone={fo?.source === "operator" ? "text-avail" : fo?.source === "pjp" ? "text-cyan" : "text-white"}
          testId="ops-fuel-origin"
        />
        <OpsCell
          label={`Fuel · ${fd?.airport_icao || "destination"}`}
          value={fd?.price_per_gal_usd != null ? `$${fd.price_per_gal_usd.toFixed(2)}/gal` : "—"}
          sub={fd ? sourceLabel(fd.source) : "no data"}
          tone={fd?.source === "operator" ? "text-avail" : fd?.source === "pjp" ? "text-cyan" : "text-white"}
          testId="ops-fuel-destination"
        />
        <OpsCell
          label="Tanker savings"
          value={tanker?.eligible ? `${fmtUsd(tanker.savings_usd)}` : "—"}
          sub={tanker?.eligible
            ? `${Math.round(tanker.tanker_gallons).toLocaleString()} gal · $${tanker.delta_per_gal}/gal spread`
            : tanker?.reason === "leg_exceeds_range" ? "fuel stop required"
            : "no favorable spread"}
          tone={tanker?.eligible ? "text-avail" : "text-white/40"}
          testId="ops-tanker"
        />
      </div>

      {tanker?.eligible && (
        <div className="mt-2.5 text-[11px] text-white/70 leading-relaxed">
          <b className="text-avail">Savvy operator tip:</b> tanker {Math.round(tanker.tanker_gallons).toLocaleString()} gal from{" "}
          <b className="text-cyan">{fo?.airport_icao}</b> (${fo?.price_per_gal_usd?.toFixed(2)}/gal) to skip the{" "}
          <b>${fd?.price_per_gal_usd?.toFixed(2)}/gal</b> at destination — <b className="text-avail">{fmtUsd(tanker.savings_usd)} saved</b> on this leg.
        </div>
      )}
    </div>
  );
}

function OpsCell({ label, value, sub, tone, testId }) {
  return (
    <div className="rounded bg-obsidian/60 border border-white/10 px-2 py-1.5" data-testid={testId}>
      <div className="mono text-[9px] tracking-[0.24em] uppercase text-white/50">{label}</div>
      <div className={`mono text-[13px] mt-0.5 ${tone}`}>{value}</div>
      <div className="mono text-[9px] tracking-widest uppercase text-white/35 mt-0.5">{sub}</div>
    </div>
  );
}

/* ======================== OPERATOR RFQ BLAST CTA ======================== *
 * Below the market band we invite the customer to send this exact trip to
 * every PJP operator flying that aircraft type. Non-members are routed to
 * the Charter Client Membership upsell page ($5,000/yr) so they see the
 * value prop and pricing before any inline capture. Charter Client members
 * get the inline form (existing behavior). The route carries the trip
 * context (aircraft type + route) via query params so the upsell page can
 * address the shopper by exactly what they just priced.
 */
function OperatorRfqCta({ quote, multileg = false }) {
  const nav = useNavigate();
  const { user } = useAuth();
  // Only a paid Stripe subscription unlocks the inline RFQ form. Just
  // picking the "Charter Client" hat at signup ≠ member — see /charter-
  // membership for the pitch + checkout.
  const isCharterMember = user?.membership_tier_slug === "charter_client";

  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", phone: "", notes: "" });
  const [done, setDone] = useState(false);

  const routeLabel = multileg
    ? quote.legs.map((l) => l.origin.icao).concat([quote.legs[quote.legs.length - 1].destination.icao]).join(" → ")
    : `${quote.origin.icao} → ${quote.destination.icao}`;

  const goToUpsell = () => {
    // Serialise the exact payload POST /api/charter-requests expects, so we
    // can replay it the instant the shopper finishes signup + Stripe.
    // See /app/frontend/src/lib/pendingRfq.js for the replay flow.
    let routing, departure_icao, destination_icao, trip_start;
    if (multileg) {
      routing = quote.legs.map((l) => l.origin.icao);
      routing.push(quote.legs[quote.legs.length - 1].destination.icao);
      departure_icao = routing[0];
      destination_icao = routing[routing.length - 1];
      trip_start = quote.legs[0].departure_date || new Date().toISOString().slice(0, 10);
    } else {
      routing = [quote.origin.icao, quote.destination.icao];
      departure_icao = quote.origin.icao;
      destination_icao = quote.destination.icao;
      trip_start = (quote.departure_date || new Date().toISOString()).slice(0, 10);
    }
    stashPendingRfq({
      // Placeholder contact fields — the /register step will overwrite these
      // with the authenticated user's own name/email before the replay hits
      // the server. Kept here as safe defaults for defence-in-depth.
      contact_name: "Charter Client",
      contact_email: "pending@pjp.local",
      contact_phone: null,
      departure_icao,
      destination_icao,
      routing,
      trip_start,
      passengers: quote.passengers || 1,
      aircraft_class_pref: quote.aircraft.type_code,
      budget_usd: quote.market_range_usd?.median_usd || Math.round(quote.total_usd),
      special_requests: `Auto-quote median ${quote.market_range_usd ? "$"+quote.market_range_usd.median_usd.toLocaleString() : "$"+Math.round(quote.total_usd).toLocaleString()} — please quote against this itinerary.`,
      pets: false,
      route_label: routeLabel,   // display-only convenience for the upsell page
    });
    const qs = new URLSearchParams({
      type: quote.aircraft.type_code || "",
      route: routeLabel,
    }).toString();
    nav(`/charter-membership?${qs}`);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim()) {
      toast.error("Name and email are required to route your RFQ.");
      return;
    }
    setBusy(true);
    try {
      let routing, departure_icao, destination_icao, trip_start;
      if (multileg) {
        routing = quote.legs.map((l) => l.origin.icao);
        routing.push(quote.legs[quote.legs.length - 1].destination.icao);
        departure_icao = routing[0];
        destination_icao = routing[routing.length - 1];
        trip_start = quote.legs[0].departure_date || new Date().toISOString().slice(0, 10);
      } else {
        routing = [quote.origin.icao, quote.destination.icao];
        departure_icao = quote.origin.icao;
        destination_icao = quote.destination.icao;
        trip_start = (quote.departure_date || new Date().toISOString()).slice(0, 10);
      }

      await api.post("/charter-requests", {
        contact_name: form.name.trim(),
        contact_email: form.email.trim(),
        contact_phone: form.phone.trim() || null,
        departure_icao,
        destination_icao,
        routing,
        trip_start,
        passengers: quote.passengers || 1,
        aircraft_class_pref: quote.aircraft.type_code,
        budget_usd: quote.market_range_usd?.median_usd || Math.round(quote.total_usd),
        special_requests: form.notes.trim() || `Auto-quote median ${quote.market_range_usd ? "$"+quote.market_range_usd.median_usd.toLocaleString() : "$"+Math.round(quote.total_usd).toLocaleString()} — please quote against this itinerary.`,
        pets: false,
      });
      setDone(true);
      toast.success("RFQ sent to matching PJP operators.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to send RFQ.");
    } finally {
      setBusy(false);
    }
  };

  if (done) {
    return (
      <div className="mt-4 rounded-lg border border-avail/40 bg-avail/[0.08] p-4" data-testid="quote-rfq-done">
        <div className="mono text-[10px] tracking-[0.24em] uppercase text-avail mb-1">// RFQ dispatched</div>
        <div className="text-sm text-white/85">
          Your trip is en route to operators flying <b>{quote.aircraft.type_code}</b>. Bids typically land within 20–90 minutes during business hours. We'll email <span className="text-cyan">{form.email}</span> as soon as the first quote comes in.
        </div>
      </div>
    );
  }

  // Non-members → route to /charter-membership upsell page.
  if (!isCharterMember) {
    return (
      <div className="mt-4 rounded-lg border border-cyan/40 bg-cyan/[0.05] p-4" data-testid="quote-rfq-cta">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div className="flex-1 min-w-0">
            <div className="mono text-[10px] tracking-[0.24em] uppercase text-cyan/80">// Skip the guessing</div>
            <div className="text-sm text-white/90 mt-0.5">
              Send this trip to every PJP operator flying a <b className="text-cyan">{quote.aircraft.type_code}</b> and receive live bids in your inbox.
            </div>
          </div>
          <button
            type="button"
            onClick={goToUpsell}
            data-testid="quote-rfq-open"
            className="btn-hud btn-hud-solid"
          >
            <Users className="h-3.5 w-3.5" /> GET OPERATOR DIRECT BIDS <ArrowRight className="h-3.5 w-3.5" />
          </button>
        </div>
      </div>
    );
  }

  // Charter-client members get the inline RFQ form.
  return (
    <div className="mt-4 rounded-lg border border-cyan/40 bg-cyan/[0.05] p-4" data-testid="quote-rfq-cta">
      {!open ? (
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <div className="mono text-[10px] tracking-[0.24em] uppercase text-cyan/80">// Member RFQ · zero markup</div>
            <div className="text-sm text-white/90 mt-0.5">
              Send this trip to every PJP operator flying a <b className="text-cyan">{quote.aircraft.type_code}</b> — live bids to your inbox.
            </div>
          </div>
          <button
            type="button"
            onClick={() => setOpen(true)}
            data-testid="quote-rfq-open"
            className="btn-hud btn-hud-solid"
          >
            <Users className="h-3.5 w-3.5" /> GET OPERATOR DIRECT BIDS <ArrowRight className="h-3.5 w-3.5" />
          </button>
        </div>
      ) : (
        <form onSubmit={submit} className="space-y-3" data-testid="quote-rfq-form">
          <div className="mono text-[10px] tracking-[0.24em] uppercase text-cyan/80">// Send RFQ to PJP operators · {quote.aircraft.type_code}</div>
          <div className="grid md:grid-cols-2 gap-3">
            <div>
              <div className="mono text-[9px] tracking-widest uppercase text-white/50 mb-1">Full name *</div>
              <input data-testid="quote-rfq-name" required className="input w-full" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Jane Pilot" />
            </div>
            <div>
              <div className="mono text-[9px] tracking-widest uppercase text-white/50 mb-1">Email *</div>
              <input data-testid="quote-rfq-email" required type="email" className="input w-full" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="you@company.com" />
            </div>
            <div>
              <div className="mono text-[9px] tracking-widest uppercase text-white/50 mb-1">Phone (optional)</div>
              <input data-testid="quote-rfq-phone" className="input w-full" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="+1 555 …" />
            </div>
            <div>
              <div className="mono text-[9px] tracking-widest uppercase text-white/50 mb-1">Notes for operators (optional)</div>
              <input data-testid="quote-rfq-notes" className="input w-full" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} placeholder="Catering, pets, ground …" />
            </div>
          </div>
          <div className="flex items-center gap-2 justify-end">
            <button type="button" onClick={() => setOpen(false)} data-testid="quote-rfq-cancel" className="mono text-[10px] tracking-widest uppercase px-3 py-2 rounded border border-white/15 text-white/70 hover:bg-white/5">Cancel</button>
            <button type="submit" disabled={busy} data-testid="quote-rfq-submit" className="btn-hud btn-hud-solid disabled:opacity-50">
              {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Users className="h-3.5 w-3.5" />}
              {busy ? "SENDING…" : "SEND RFQ"}
            </button>
          </div>
        </form>
      )}
    </div>
  );
}

/* ======================== MULTI-LEG RESULT ======================== */

function MultiLegResult({ data }) {
  const fmt = (n) => n.toLocaleString(undefined, { style: "currency", currency: "USD", maximumFractionDigits: 0 });
  const t = data.totals;
  return (
    <div className="mt-4 rounded-xl border border-white/10 p-5" data-testid="quote-result-multi" style={{ background: "rgba(5,8,16,0.7)" }}>
      {/* Trip summary */}
      <div className="flex items-center gap-3 flex-wrap">
        <Route className="h-4 w-4 text-cyan" />
        <div className="mono text-[10px] tracking-widest uppercase text-cyan/80">// Multi-leg itinerary · {data.legs.length} legs</div>
        <div className="ml-auto flex flex-wrap gap-3 text-right">
          <FactBadge k="Total distance" v={`${t.distance_nm.toLocaleString()} nm`} />
          <FactBadge k="Raw hours" v={`${t.raw_flight_hours} h`} />
          <FactBadge k="Billed hours" v={`${t.billed_flight_hours} h`} />
          <FactBadge k="Overnights" v={t.overnights} />
        </div>
      </div>

      {/* Aircraft strip */}
      <div className="mt-3 rounded-lg border border-white/[0.06] bg-white/[0.02] p-3 flex flex-wrap items-center gap-3 text-[11px]">
        <span className="mono uppercase tracking-widest text-cyan/80">{data.aircraft.type_code}</span>
        <span className="text-white/50">Cruise <span className="text-white">{data.aircraft.cruise_ktas} kt</span></span>
        <span className="text-white/50">Rate <span className="text-white">${data.aircraft.hourly_rate_usd.toLocaleString()}/hr</span></span>
        <span className="ml-auto mono text-[9px] tracking-widest uppercase text-white/40">
          Rate source: {data.aircraft.rate_source === "aircraft" ? "aircraft" : "type median"}
        </span>
      </div>

      {/* Leg-by-leg table */}
      <div className="mt-4 space-y-1.5" data-testid="quote-multi-legs-detail">
        {data.legs.map((l) => {
          const warnings = (data.duty_warnings || []).filter((w) => w.leg_index === l.index);
          return (
            <React.Fragment key={l.index}>
              <div className="grid grid-cols-[28px_1fr_120px_140px_100px_120px] gap-2 items-center text-[12px] py-1.5 border-b border-white/[0.04]">
                <div className="mono text-[10px] tracking-widest uppercase text-cyan/70">L{l.index}</div>
                <div className="min-w-0">
                  <div className="text-white/85 truncate">
                    <span className="mono text-cyan">{l.origin.icao}</span>
                    <span className="mx-1 text-white/40">→</span>
                    <span className="mono text-cyan">{l.destination.icao}</span>
                    <span className="text-white/50 ml-2 text-[11px]">{l.origin.city} → {l.destination.city}</span>
                  </div>
                  <div className="mono text-[10px] tracking-widest uppercase text-white/40">{l.departure_local || "—"}</div>
                </div>
                <div className="mono text-white/70">{l.distance_nm} nm</div>
                <div className="mono text-white/70">{l.raw_flight_hours} h</div>
                <div className="mono text-white/50">{l.passengers} pax</div>
                <div className="mono text-white text-right">{fmt(l.leg_flight_cost_usd)}</div>
              </div>
              {warnings.map((w, wi) => (
                <div key={wi}
                  data-testid={`quote-duty-warning-${l.index}`}
                  className={`ml-8 mb-1 rounded p-2.5 text-[11px] border ${
                    w.level === "block"
                      ? "border-red-500/50 bg-red-500/[0.06]"
                      : "border-amber-400/50 bg-amber-400/[0.06]"
                  }`}
                >
                  <div className={`mono text-[10px] tracking-widest uppercase flex items-center gap-1.5 ${
                    w.level === "block" ? "text-red-300" : "text-amber-300"
                  }`}>
                    {w.level === "block" ? "! DUTY BREACH" : "▲ DUTY WARNING"} · {w.rule}
                  </div>
                  <div className="text-white/80 mt-1 leading-relaxed">{w.message}</div>
                  <div className="mt-1.5">
                    <a href={w.far_url} target="_blank" rel="noreferrer"
                      data-testid={`quote-far-link-${l.index}`}
                      className="mono text-[10px] tracking-widest uppercase text-cyan hover:text-white inline-flex items-center gap-1">
                      Read FAR 135.267 <span aria-hidden>↗</span>
                    </a>
                  </div>
                </div>
              ))}
            </React.Fragment>
          );
        })}
      </div>

      {/* Day-by-day billed hours */}
      {t.daily_minimum_applies && data.days_billing.some((d) => d.minimum_applied) && (
        <div className="mt-4 rounded-lg border border-amber-400/40 bg-amber-400/[0.06] p-3">
          <div className="mono text-[10px] tracking-widest uppercase text-amber-300 mb-2 flex items-center gap-1">
            <Clock className="h-3 w-3" /> Daily {t.daily_minimum_hours}h minimum applied · trip under {t.daily_minimum_waiver_at_hours}h total
          </div>
          <div className="space-y-1 text-[12px]">
            {data.days_billing.map((d) => (
              <div key={d.date} className="flex items-center justify-between">
                <span className="mono text-white/60">{d.date}</span>
                <span className="text-white/60">
                  Raw <span className="mono text-white/80">{d.raw_hours}h</span> · Billed{" "}
                  <span className={`mono ${d.minimum_applied ? "text-amber-300" : "text-white"}`}>{d.billed_hours}h</span>
                  {d.minimum_applied && <span className="ml-1 mono text-[9px] tracking-widest uppercase text-amber-300">(min)</span>}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Cost breakdown */}
      <div className="mt-4 grid md:grid-cols-2 gap-3">
        <div className="space-y-1.5">
          <LineItem label={`Flight hours (${t.billed_flight_hours}h billed)`} value={fmt(data.breakdown.flight_hours_cost_usd)} />
          {data.breakdown.overnight_fees_usd > 0 && (
            <LineItem label={`Crew overnights × ${t.overnights}`} value={fmt(data.breakdown.overnight_fees_usd)} muted />
          )}
          <LineItem label="Federal excise tax (7.5%)" value={fmt(data.breakdown.federal_excise_tax_usd)} muted />
          <LineItem label={`Segment fees × ${data.legs.length}`} value={fmt(data.breakdown.segment_fee_usd)} muted />
          <LineItem label="Fuel surcharge" value={fmt(data.breakdown.fuel_surcharge_usd)} muted />
        </div>
        <div className="rounded-lg border border-cyan/40 p-4 bg-cyan/[0.08] flex flex-col justify-center">
          <div className="mono text-[10px] tracking-widest uppercase text-cyan/80">PJP market median (one aircraft)</div>
          <div className="font-display text-4xl font-bold text-white mt-1 leading-none" data-testid="quote-multi-total">
            {fmt(data.total_usd)}
          </div>
          <div className="mono text-[10px] tracking-widest uppercase text-white/50 mt-1.5">
            {data.compare_savings_usd > 0
              ? <>Cheaper than {data.legs.length}× one-ways by <span className="text-avail">{fmt(data.compare_savings_usd)}</span></>
              : data.compare_savings_usd < 0
                ? <>Convenience premium vs {data.legs.length}× one-ways: <span className="text-amber-300">{fmt(Math.abs(data.compare_savings_usd))}</span></>
                : <>Same price as {data.legs.length}× one-ways</>}
          </div>
        </div>
      </div>

      {/* Market range band */}
      <MarketRangeBar range={data.market_range_usd} median={data.total_usd} />

      {/* Operator RFQ CTA */}
      <OperatorRfqCta quote={data} multileg />

      {(data.notes || []).length > 0 && (
        <ul className="mt-4 space-y-1 text-[11px] text-white/55">
          {data.notes.map((n, i) => (
            <li key={i} className="flex gap-1.5"><span className="text-white/30">·</span>{n}</li>
          ))}
        </ul>
      )}
    </div>
  );
}
